// The inbox: a conversation list on the left, the open conversation on the
// right. Mounted on inbox_messages#inbox by the page module.
//
// The list holds each conversation's newest message (one InboxMessageRecord
// per conversation_key) and is patched in place as sends and polls land, so
// previews stay current without refetching. New conversations can only go to
// the one recipient the server hands us (the founder, for now) — when it
// hands us nobody, the new-message affordance disappears.
import { createRoot } from "react-dom/client"
import { type JSX, useEffect, useState } from "react"
import { fetchLatestMessages, type InboxMessageRecord, type InboxUser, sendInboxMessage } from "./inbox/api"
import { ConversationPane } from "./inbox/conversation_pane"

type View =
  | { kind: "none" }
  | { kind: "conversation"; conversationKey: string }
  | { kind: "new" }

type InboxProps = {
  latestUrl: string
  messagesUrl: string
  newRecipient: InboxUser | null
  composeOnArrival: boolean
}

function InboxApp({ latestUrl, messagesUrl, newRecipient, composeOnArrival }: InboxProps): JSX.Element {
  const [conversations, setConversations] = useState<InboxMessageRecord[]>([])
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  // Arriving from a profile's "Message this user" button (?to=<handle>)
  // means the composer is the whole reason we're here — open it.
  const [view, setView] = useState<View>(
    composeOnArrival && newRecipient ? { kind: "new" } : { kind: "none" }
  )

  useEffect(() => {
    let cancelled = false
    void fetchLatestMessages(latestUrl).then((latest) => {
      if (cancelled) return
      if (latest) setConversations(latest)
      else setFailed(true)
      setLoaded(true)

      // We were sent here to write to someone we already have a thread with:
      // continue it rather than opening a blank composer beside it.
      if (composeOnArrival && newRecipient && latest) {
        const existing = latest.find((c) => c.other_user?.id === newRecipient.id)
        if (existing) setView({ kind: "conversation", conversationKey: existing.conversation_key })
      }
    })
    return () => {
      cancelled = true
    }
  }, [latestUrl, composeOnArrival, newRecipient])

  // A message just arrived or was sent: it becomes its conversation's
  // preview, in place for a known conversation, on top for a new one.
  function absorbLatest(message: InboxMessageRecord): void {
    setConversations((current) => {
      const known = current.some((c) => c.conversation_key === message.conversation_key)
      if (!known) return [ message, ...current ]
      return current.map((c) => (c.conversation_key === message.conversation_key ? message : c))
    })
  }

  // The first send of a new conversation: absorb it, then jump into the
  // conversation it just created.
  function openNewConversation(message: InboxMessageRecord): void {
    absorbLatest(message)
    setView({ kind: "conversation", conversationKey: message.conversation_key })
  }

  const selected = view.kind === "conversation"
    ? conversations.find((c) => c.conversation_key === view.conversationKey) ?? null
    : null

  return (
    <div className={`inbox${view.kind === "none" ? "" : " inbox--open"}`}>
      <nav className="inbox__list" aria-label="Conversations">
        <button
          type="button"
          className={`inbox__preview inbox__preview--new${view.kind === "new" ? " is-selected" : ""}`}
          aria-current={view.kind === "new" ? "true" : undefined}
          onClick={() => setView({ kind: "new" })}
        >
          <span className="inbox__preview-name">+ New message</span>
          <span className="inbox__preview-snippet">Start a conversation</span>
        </button>

        {failed && <p className="inbox__list-note" role="alert">Could not load your inbox.</p>}
        {loaded && !failed && conversations.length === 0 && (
          <p className="inbox__list-note">No conversations yet.</p>
        )}

        {conversations.map((conversation) => (
          <ConversationPreview
            key={conversation.conversation_key}
            conversation={conversation}
            isSelected={view.kind === "conversation" && view.conversationKey === conversation.conversation_key}
            onSelect={() => setView({ kind: "conversation", conversationKey: conversation.conversation_key })}
          />
        ))}
      </nav>

      {view.kind === "conversation" && selected && (
        <ConversationPane
          key={selected.conversation_key}
          messagesUrl={messagesUrl}
          conversationKey={selected.conversation_key}
          otherUser={selected.other_user}
          onLatest={absorbLatest}
          onBack={() => setView({ kind: "none" })}
        />
      )}
      {view.kind === "new" && (
        <NewConversationPane
          messagesUrl={messagesUrl}
          defaultRecipient={newRecipient}
          onCreated={openNewConversation}
          onBack={() => setView({ kind: "none" })}
        />
      )}
      {(view.kind === "none" || (view.kind === "conversation" && !selected)) && (
        <section className="inbox__pane inbox__pane--empty" aria-label="No conversation selected">
          <p className="inbox__select-note">Select a conversation to view more.</p>
        </section>
      )}
    </div>
  )
}

function ConversationPreview({
  conversation,
  isSelected,
  onSelect,
}: {
  conversation: InboxMessageRecord
  isSelected: boolean
  onSelect: () => void
}): JSX.Element {
  const name = conversation.other_user?.name ?? "Someone"
  const username = conversation.other_user?.username
  return (
    <button
      type="button"
      className={`inbox__preview${isSelected ? " is-selected" : ""}`}
      aria-current={isSelected ? "true" : undefined}
      onClick={onSelect}
    >
      <span className="inbox__preview-name">
        {name}
        {username && <span className="inbox__preview-username">@{username}</span>}
      </span>
      <span className="inbox__preview-snippet">{conversation.contents ?? ""}</span>
    </button>
  )
}

// Composing the first message of a conversation. "To" is a free-text handle
// the server resolves — a public id today, a username once those exist. The
// server-suggested recipient (the founder, for now) prefills it so the happy
// path is just writing the message.
function NewConversationPane({
  messagesUrl,
  defaultRecipient,
  onCreated,
  onBack,
}: {
  messagesUrl: string
  defaultRecipient: InboxUser | null
  onCreated: (message: InboxMessageRecord) => void
  onBack: () => void
}): JSX.Element {
  const [to, setTo] = useState(defaultRecipient?.username ?? defaultRecipient?.id ?? "")
  const [draft, setDraft] = useState("")
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function send(): Promise<void> {
    const recipient = to.trim()
    const contents = draft.trim()
    if (!recipient || !contents || sending) return
    setSending(true)
    const result = await sendInboxMessage(messagesUrl, recipient, contents)
    setSending(false)

    if (typeof result === "string") {
      setProblem(result)
      return
    }
    onCreated(result)
  }

  // Writing to the suggested recipient shows who's behind the handle.
  const toHandle = to.trim().toLowerCase()
  const toName = defaultRecipient &&
    (toHandle === defaultRecipient.id.toLowerCase() || toHandle === defaultRecipient.username?.toLowerCase())
    ? defaultRecipient.name
    : null

  return (
    <section className="inbox__pane" aria-label="New message">
      <header className="inbox__pane-head">
        <button type="button" className="inbox__back" aria-label="Back to conversations" onClick={onBack}>
          ←
        </button>
        <h1 className="inbox__pane-title">New message</h1>
      </header>

      {problem && <p className="inbox__problem" role="alert">{problem}</p>}

      <form
        className="inbox__composer inbox__composer--new"
        onSubmit={(event) => {
          event.preventDefault()
          void send()
        }}
      >
        <label className="inbox__new-to">
          To:
          <input
            type="text"
            className="inbox__input inbox__input--to"
            placeholder="username"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
          {toName && <span className="inbox__new-to-name">{toName}</span>}
        </label>
        <textarea
          className="inbox__input inbox__input--area"
          rows={4}
          placeholder="Write your message…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button type="submit" className="btn btn--primary" disabled={sending || !to.trim() || !draft.trim()}>
          {sending ? "Sending…" : "Send message"}
        </button>
      </form>
    </section>
  )
}

function parseRecipient(raw: string | undefined): InboxUser | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === "object" && "id" in parsed) return parsed as InboxUser
  } catch {
    // fall through
  }
  return null
}

export function mountInboxApp(): void {
  const el = document.querySelector<HTMLElement>("[data-inbox-app]")
  if (!el) return

  createRoot(el).render(
    <InboxApp
      latestUrl={el.dataset.inboxLatestUrl ?? ""}
      messagesUrl={el.dataset.inboxMessagesUrl ?? ""}
      newRecipient={parseRecipient(el.dataset.inboxNewRecipient)}
      composeOnArrival={el.dataset.inboxCompose === "true"}
    />
  )
}
